<template>
	<TaskListRowTask
		:task="task"
		:col-definition="colDefinition"
		:subtasks-count="subtasksCount"
		:show-subtasks="showSubtasks"
		:has-uncompleted-subtasks="hasUncompletedSubtasks"
		:task-enrichment-data="taskEnrichmentData"
		@task-updated="emit('task-updated', $event)"
	/>

	<template v-if="showSubtasks && isOpen">
		<template v-if="subtasks && subtasks.length > 0">
			<TaskListRowTask
				v-for="subtask in subtasks"
				:key="subtask.id"
				:task="subtask"
				:col-definition="colDefinition"
				is-subtask
				:show-subtasks="showSubtasks"
				:task-enrichment-data="getSubtaskEnrichmentData(subtask.id)"
				@task-updated="emit('task-updated', $event)"
			/>
		</template>

		<template v-if="subtasksUnsorted && subtasksUnsorted[task.id]">
			<TaskListRowTask
				v-for="subtask in subtasksUnsorted[task.id]"
				:key="subtask.id"
				:task="subtask"
				:col-definition="colDefinition"
				is-subtask
				:show-subtasks="showSubtasks"
				:task-enrichment-data="getSubtaskEnrichmentData(subtask.id)"
				@task-updated="emit('task-updated', $event)"
			/>
		</template>

		<template v-if="loadingSubtasks && loadingSubtasks[task.id]">
			<TaskListRowSkeleton
				v-for="i in loadingSubtasks[task.id]"
				:key="i"
				:col-definition="colDefinition"
				:name="i"
				is-subtask
				animate
				:show-subtasks="showSubtasks"
			/>
		</template>

		<TaskListRowTaskBundle
			v-if="taskListStore.subtaskBundleOpen === task.id"
			:parent-task-id="task.id"
			:show-subtasks="showSubtasks"
			:col-definition="colDefinition"
		/>

		<TaskListRowTaskAddTask
			v-show="canAddSubtasks"
			is-subtask
			:task-id="task.id"
			:show-subtasks="showSubtasks"
			:col-definition="colDefinition"
			:add-task-label="$t('quickadd_subtask')"
			@add="onAddTask($event)"
		/>
	</template>
</template>

<script setup lang="ts">
import { computed, toRefs } from 'vue';
import {
	type Task,
	type ProjectPhase,
	TYPES,
	type TaskListTaskFieldsFragment,
	type LimitedTaskFieldsFragment,
	type TaskListTaskFragment,
	type TaskListEnrichmentFieldsFragment,
} from '@/generated/graphql';
import type { SuiDataTableColumnType } from '@developers/scoro-ui';
import TaskListRowTask from './TaskListRowTask.vue';
import TaskListRowTaskAddTask from './TaskListRowAddTask.vue';
import TaskListRowSkeleton from './TaskListRowSkeleton.vue';
import TaskListRowTaskBundle from './TaskListRowTaskBundle.vue';
import { type ShowEventPopupPayload } from '@/composables/useMfeUtils';
import {
	type TasListTaskOrLimited,
	type TaskListLoadingSubtaskRows,
} from '../types';
import { useLocalStorage } from '@vueuse/core';
import { LocalStorageKeys } from '@/constants/storage';
import { useTaskListStore } from '../store';
import { getTaskEnrichmentData } from '../utils';

export type TaskListRowTaskProps = {
	task: TasListTaskOrLimited;
	colDefinition?: SuiDataTableColumnType[];
	subtasks?: Array<TaskListTaskFieldsFragment | LimitedTaskFieldsFragment>;
	showSubtasks?: boolean;
	subtasksUnsorted?: Record<Task['id'], TasListTaskOrLimited[]>;
	loadingSubtasks?: TaskListLoadingSubtaskRows;
	tasksEnrichmentData?: Map<
		Task['id'],
		TaskListEnrichmentFieldsFragment
	> | null;
	newlyAddedTaskIds?: Set<Task['id']>;
};

const props = defineProps<TaskListRowTaskProps>();
const emit = defineEmits<{
	(e: 'show-event-popup', payload: ShowEventPopupPayload): void;
	(e: 'task-updated', task?: TaskListTaskFragment): void;
	(
		e: 'add-task',
		payload: { name: Task['name']; projectPhaseId?: ProjectPhase['id'] }
	): void;
}>();

const { task, subtasksUnsorted } = toRefs(props);
const taskListStore = useTaskListStore();
const openTaskIds = useLocalStorage(
	LocalStorageKeys.SubtaskListOpenTasks,
	[] as Array<Task['id']>
);

const isOpen = computed(() =>
	openTaskIds.value.some((id) => id === task.value.id)
);

const taskEnrichmentData = computed(() => {
	return getTaskEnrichmentData(
		props.task.id,
		props.tasksEnrichmentData,
		props.newlyAddedTaskIds
	);
});

const subtasksCount = computed(() => {
	return (
		(task.value.subtasks?.length || 0) +
		(subtasksUnsorted.value?.[task.value.id]?.length || 0)
	);
});

const hasUncompletedSubtasks = computed(
	() =>
		task.value.subtasks?.some((subtask) => !subtask.isDone) ||
		(subtasksUnsorted.value?.[task.value.id]?.length ?? 0) > 0
);

const canAddSubtasks = computed(() => {
	return (
		task.value.permissions?.canModify &&
		((props.task.__typename === TYPES.Task && !props.task.isDone) ||
			props.task.__typename === TYPES.LimitedTask)
	);
});

const getSubtaskEnrichmentData = (subtaskId: Task['id']) => {
	return getTaskEnrichmentData(
		subtaskId,
		props.tasksEnrichmentData,
		props.newlyAddedTaskIds
	);
};

const onAddTask = async (name: string, projectPhaseId?: ProjectPhase['id']) => {
	emit('add-task', {
		name,
		projectPhaseId,
	});
};
</script>
